import {ActivatedRouteSnapshot} from '@angular/router';
import {resolveDatasetsDetailFromRoute} from '../../services/resolvers/garfield-datasets-detail.resolver';
import {ReportKind} from './report-kind';
import {RouteParser} from '../route-parser';
import {Memoize} from 'typescript-memoize';
import {
	ByExpenditureObjectsDisplayOption, DisplayUnitDisplayOption,
	ExcludeInternalServiceFundsOption,
	Option, SelectedFundOption, ShowAccountNumbersDisplayOption,
	SingleYearReportOption,
} from './option';
import {DatasetDetail} from '../../services/api/fit-api/models/datasets/dataset-type';
import {UserAccessLevel} from '../report';
import {Hint, HintInformation} from '../hintInformation';
import {User} from '../user';
import {resolveGarfieldFiledFundNodes} from '../../services/resolvers/garfield-filed-fund-nodes.resolver';
import {GarfieldFundNode} from '../../components/garfield-funds-selector/garfield-fund-node-model';
import {type GarfieldFinancialReportService} from '../../pages/financial-report/garfield-financial-report.service';
import {HierarchyConfig} from '../data-cube/hierarchy-config';
import {type GarfieldFinancialSummaryService} from '../../services/garfield-financial-summary.service';
import {lastValueFrom, Observable} from 'rxjs';
import {BarsAggregation} from '../../services/api/fit-api/models/bars-aggregation';
import {resolveGarfieldLocalGovernments} from '../../services/resolvers/garfield-local-governments.resolver';
import {LocalGovernment} from '../../services/api/fit-api/models/local-government';
import {InvalidGovernment} from '../invalid-government';
import {SnapshotWithDetail} from '../../services/api/fit-api/models/datasets/snapshot';
import {FundType} from '../../services/api/fit-api/models/funds/fund-type';
import {FundCategory} from '../../services/api/fit-api/models/funds/fund-category';
import {LiveWithDetail} from '../../services/api/fit-api/models/datasets/live';
import {GarfieldOptionsBundle} from './garfield-options-bundle';
import {ReferenceLookup} from '../../services/api/fit-api/models/snapshots/reference-lookup';
import {BonanzaSchedule9Type} from '../../services/api/fit-api/models/snapshots/bonanza-schedule-9-type';
import {DebtCategoryItem} from '../../services/api/fit-api/models/snapshots/debt-category-item';
import {resolveDebtReferenceLookups} from '../../services/resolvers/garfield-debt-reference-lookups.resolver';

/**
 * Report
 *
 * Base class for Report Instances
 *
 * Notes:
 * - options getter returns a concatenated list of report options and context options
 */
// Bridge pattern
// https://refactoring.guru/design-patterns/bridge
export interface ReportContext {
	getRowHierarchy(financialReportService: GarfieldFinancialReportService): HierarchyConfig<any>;
	getColumnHierarchy(fundCategories: FundCategory[], fundTypes: FundType[], financialReportService: GarfieldFinancialReportService): Observable<HierarchyConfig<any>>;
	getData(financialSummaryService: GarfieldFinancialSummaryService): Observable<any[]>;
	supports: Array<ShallowReport>;
	options: Array<new(...args: any[]) => Option>;
	reportRef: GarfieldReport | null;
}

// class GovernmentFinancesContext implements ReportContext {
// }
//
// class GovernmentTypeFinancesContext implements ReportContext {
// }

/**
 * 1) Data source fetch that needs to happen if any of this listed params change for url navigation events (i.e. user changes options or report selection),
 * likewise if none of these change there should be no new data fetch:
 * - report kind - required route param - odata filter on fsSectionId is part of the report definition and is unique across report or different data fetch (i.e. schedule1 aggregations vs debt and reliabilities fetch)\
 * - govs list - required route param - odata filter on mcag "contained in list"
 * Assumption: in any context, required route params are always part of the list of params that require a new data fetch
 * - single year selection - optional route param - odata filter on year
 * - exclude internal service fund - optional route param - remove fundTypeId 5 from odata filter
 * - selected fund - optional route param - odata filter on selected fund node (category or type)\
 * Note: by expenditure object detail is always returned from fetch (in case its needed by the selected row hierarchy); this is by design,
 * 	however we could change the fetch to include/not include expenditure object detail if we wanted to
 *
 * 2) Data cube generation/regeneration (change in any of the following):
 * - row hierarchy
 * 	  - report kind - required route param - financial summary and debt and liabilities have different row configurations then the rest of the reports
 *    - datasets - required route param - provides lookups for row hierarchy configuration
 *    - by expenditure object - optional route param - show expenditure object row config if true for applicable reports (expenditures and expenditures and other decreases)
 * - column hierarchy
 *    - govs list - required route param - resolved from route to display common sortable name in column header
 *    - selected fund - optional route param - for column header display
 *    - fundCategories and fundTypes - used for fun lookup for column header display
 * 	- exclude internal service fund - optional route param - on the report options. Signifies which amount field to aggregate (e.g. 'totalAmount' or 'totalAmountExclIntlSrvc')
 * 	- change in data source fetch (see above)
 * 	- hasGrandTotals - static property on the report - either aggregate grand total row data or do not
 *
 * 	3) Pivot Table generation/regeneration (change in any of the following):
 * 	- data cube - see above
 * 	- empty cell annotations - datasets, local governments, and single year selection
 * 	- (note: display unit is an input to generate the pivot table but changes to the display unit alone does not require regeneration of the pivot table, i.e. PivotTable.updateDisplayUnit())
 *
 * 	4) Pivot Table updates that don't cause regeneration
 * 	- account code toggle - i.e. PivotTable.updateAccountCodes() based on report options show account code options
 * 	- display units - PivotTable.updateDisplayUnit()
 *
 * 	Implementation decisions:
 * 	- 2 and 3 will be treated together
 * 	- Consider whether to bundle handling of 1 with 2 and 3 - changes in the by expenditure object and exclude internal service fund options don't require an updated data fetch but
 * 	doing a new data fetch when these change won't affect performance because the browser will have already cached the data
 * 	- For step 4, we are going to avoid any data fetch, data cube, or pivot table regeneration
 */
export class GovernmentComparisonContext implements ReportContext {
	reportRef: GarfieldReport | null = null;

	getRowHierarchy(financialReportService: GarfieldFinancialReportService){
		if (this.reportRef?.kind === 'debt-liabilities' && this.reportRef instanceof GarfieldDebtAndLiabilitiesReport) {
			return financialReportService.schedule9DebtCategoriesHierarchy(this.reportRef);
		} else if (this.reportRef) {
			const byExpenditureObjects = this.reportRef.options.find(o => o instanceof ByExpenditureObjectsDisplayOption)?.appliedValue;
			return financialReportService.fsSectionBarsHierarchy(this.reportRef.datasetsDetail, this.reportRef, byExpenditureObjects);
		} else {
			throw new Error('Row Hierarchy did not resolve correctly');
		}
	}

	// todo handle funds (right now only handles fund categories and fund types) when retrofitted for individual govs
	getColumnHierarchy(fundCategories: FundCategory[], fundTypes: FundType[], financialReportService: GarfieldFinancialReportService): Observable<HierarchyConfig<BarsAggregation>> {
		if (this.reportRef) {
			return financialReportService.govtComparisonColumnHierarchy(this.reportRef, fundCategories, fundTypes, financialReportService);
		} else {
			throw new Error('Column Hierarchy did not resolve correctly');
		}
	}

	// todo handle debt and liabilities when added
	getData(financialSummaryService: GarfieldFinancialSummaryService): Observable<any[]> {
		const year = this.reportRef?.options.find(o => o instanceof SingleYearReportOption)?.appliedValue;
		const fundNode = this.reportRef?.options.find(o => o instanceof SelectedFundOption)?.appliedValue;
		const excludeInternalServiceFundOption = this.reportRef?.options.find(o => o instanceof ExcludeInternalServiceFundsOption)?.appliedValue;

		if (this.reportRef?.kind === 'debt-liabilities' && this.reportRef.localGovernments && year) {
			return financialSummaryService.getSch9FinancialReportData(this.reportRef.datasetsDetail, this.reportRef, this.reportRef.localGovernments, year, year);
		} else if (this.reportRef && this.reportRef.localGovernments && year) {
			return financialSummaryService.getSch1FinancialReportData(this.reportRef.datasetsDetail, this.reportRef, this.reportRef.localGovernments, year, year, fundNode, excludeInternalServiceFundOption);
		} else {
			throw new Error('Data did not resolve correctly');
		}
	}

	supports: Array<ShallowReport> = [ShallowGarfieldSummaryReport, ShallowGarfieldRevenuesReport, ShallowGarfieldRevenuesAndOtherIncreasesReport, ShallowGarfieldExpendituresReport, ShallowGarfieldExpendituresAndOtherIncreasesReport, ShallowGarfieldDebtAndLiabilitiesReport];
	protected _optionRefs: Array<new(...args: any[]) => Option> = [SingleYearReportOption, DisplayUnitDisplayOption];

	@Memoize()
	get options() {
		return this._optionRefs;
	}
}

export abstract class GarfieldReport extends GarfieldOptionsBundle {
	abstract readonly kind: ReportKind;
	abstract readonly name: string;
	abstract readonly filterLevel: string;
	abstract readonly filterValues: number[];
	readonly hint?: Hint;
	restrictedTo?: UserAccessLevel;
	hasGrandTotals?: boolean = true; // ask about

	// Constructed ReportOptions and ContextOptions
	@Memoize()
	get options(): Array<Option> {
		const contextOptions = this.context.options?.map(option => new option(this));
		const reportOptions = this._optionRefs?.map(option => new option(this));
		return contextOptions.concat(reportOptions);
	}

	constructor(
		public override readonly route: ActivatedRouteSnapshot,
		public readonly datasetsDetail: Array<DatasetDetail>,
		public context: ReportContext,
		public readonly filedFundNodes?: GarfieldFundNode[],
		public readonly localGovernments?: LocalGovernment[]
	) {
		super(route);
		this.context.reportRef = this;
	}

	/**
	 * Factory to build the appropriate Report based on the ActivatedRoute
	 */
	static async fromRoute(route: ActivatedRouteSnapshot) {
		const [filedFundNodes, datasets, resolvedGovs, debtReferenceLookups] = await Promise.all([
			lastValueFrom(resolveGarfieldFiledFundNodes(route)),
			lastValueFrom(resolveDatasetsDetailFromRoute(route)),
			lastValueFrom(resolveGarfieldLocalGovernments(route)),
			lastValueFrom(resolveDebtReferenceLookups(route))
		]);
		const routeParser = new RouteParser(route);
		const report = routeParser.report;

		// filter resolved local govs - remove invalid govs
		const localGovernments = resolvedGovs.filter((gov: LocalGovernment | InvalidGovernment) => gov instanceof LocalGovernment);
		const context = route.data['context'];

		switch (report) {
			case ReportKind.summary:
				return new GarfieldSummaryReport(route, datasets, context, filedFundNodes, localGovernments);
			case ReportKind.revenues:
				return new GarfieldRevenuesReport(route, datasets, context, filedFundNodes, localGovernments);
			case ReportKind.revenuesOtherIncreases:
				return new GarfieldRevenuesAndOtherIncreasesReport(route, datasets, context, filedFundNodes, localGovernments);
			case ReportKind.expenditures:
				return new GarfieldExpendituresReport(route, datasets, context, filedFundNodes, localGovernments);
			case ReportKind.expendituresOtherDecreases:
				return new GarfieldExpendituresAndOtherIncreasesReport(route, datasets, context, filedFundNodes, localGovernments);
			case ReportKind.debtLiabilities:
				return new GarfieldDebtAndLiabilitiesReport(route, datasets, context, filedFundNodes, localGovernments, debtReferenceLookups);

			// note: return undefined if report not found (this way, a report that is not found can be handled by the report guard appropriately)
			default:
				return undefined;
		}
	}

	// convenience method - return applied values used to write to optional params in url
	generateOptionalParams = () => {
		return new Map(
			this.options.filter(option => !option.isDefaultValue())
				.map(option => ([option.routeParameterKey, option.appliedValue]))
		);
	}
}

// shallow reference to concrete reports (used for title dropdown)
export interface ShallowReport {
	kind: ReportKind;
	reportName: string;
	restrictedTo?: UserAccessLevel;
}

const ShallowGarfieldSummaryReport: ShallowReport = {
	kind: ReportKind.summary,
	reportName: 'Financial Summary'
}
export class GarfieldSummaryReport extends GarfieldReport {
	kind = ShallowGarfieldSummaryReport.kind;
	name = ShallowGarfieldSummaryReport.reportName;
	filterLevel = 'fsSectionId';
	filterValues = [10, 20, 25, 30, 35, 40]; // leaves off balance sheet
	override hasGrandTotals = false;
	override hint = HintInformation.FinancialReport.Summary;
	protected _optionRefs = [SelectedFundOption, ExcludeInternalServiceFundsOption, ShowAccountNumbersDisplayOption];
}

const ShallowGarfieldRevenuesReport: ShallowReport = {
	kind: ReportKind.revenues,
	reportName: 'Revenues'
}
export class GarfieldRevenuesReport extends GarfieldReport {
	kind = ShallowGarfieldRevenuesReport.kind;
	name = ShallowGarfieldRevenuesReport.reportName;
	filterLevel = 'fsSectionId';
	filterValues = [20];
	override hint = HintInformation.FinancialReport.Revenues;
	protected _optionRefs = [SelectedFundOption, ExcludeInternalServiceFundsOption, ShowAccountNumbersDisplayOption];
}

const ShallowGarfieldRevenuesAndOtherIncreasesReport: ShallowReport = {
	kind: ReportKind.revenuesOtherIncreases,
	reportName: 'Revenues and Other Increases'
}
export class GarfieldRevenuesAndOtherIncreasesReport extends GarfieldReport {
	kind = ShallowGarfieldRevenuesAndOtherIncreasesReport.kind;
	name = ShallowGarfieldRevenuesAndOtherIncreasesReport.reportName;
	filterLevel = 'fsSectionId';
	filterValues = [20, 25];
	protected _optionRefs = [SelectedFundOption, ExcludeInternalServiceFundsOption, ShowAccountNumbersDisplayOption];
}

const ShallowGarfieldExpendituresReport: ShallowReport = {
	kind: ReportKind.expenditures,
	reportName: 'Expenditures'
}
export class GarfieldExpendituresReport extends GarfieldReport {
	kind = ShallowGarfieldExpendituresReport.kind;
	name = ShallowGarfieldExpendituresReport.reportName;
	filterLevel = 'fsSectionId';
	filterValues = [30];
	override hint = HintInformation.FinancialReport.Expenditures;
	protected _optionRefs = isOnlyAnnualFiler(this.datasetsDetail)
		? [SelectedFundOption, ExcludeInternalServiceFundsOption, ShowAccountNumbersDisplayOption, ByExpenditureObjectsDisplayOption]
		: [SelectedFundOption, ExcludeInternalServiceFundsOption, ShowAccountNumbersDisplayOption];
}

const ShallowGarfieldExpendituresAndOtherIncreasesReport: ShallowReport = {
	kind: ReportKind.expendituresOtherDecreases,
	reportName: 'Expenditures and Other Decreases'
}
export class GarfieldExpendituresAndOtherIncreasesReport extends GarfieldReport {
	kind = ShallowGarfieldExpendituresAndOtherIncreasesReport.kind;
	name = ShallowGarfieldExpendituresAndOtherIncreasesReport.reportName;
	filterLevel: keyof BarsAggregation = 'fsSectionId';
	filterValues = [30, 35];
	protected _optionRefs = isOnlyAnnualFiler(this.datasetsDetail)
		? [SelectedFundOption, ExcludeInternalServiceFundsOption, ShowAccountNumbersDisplayOption, ByExpenditureObjectsDisplayOption]
		: [SelectedFundOption, ExcludeInternalServiceFundsOption, ShowAccountNumbersDisplayOption];
}

const ShallowGarfieldDebtAndLiabilitiesReport: ShallowReport = {
	kind: ReportKind.debtLiabilities,
	reportName: 'Debt & Liabilities',
	restrictedTo: UserAccessLevel.global
}
export class GarfieldDebtAndLiabilitiesReport extends GarfieldReport {
	kind = ShallowGarfieldDebtAndLiabilitiesReport.kind;
	name = ShallowGarfieldDebtAndLiabilitiesReport.reportName;
	filterLevel = '';  // No filters apply at the report level
	filterValues = []; // No filters apply at the report level
	override restrictedTo = ShallowGarfieldDebtAndLiabilitiesReport.restrictedTo;
	override hint = HintInformation.FinancialReport.DebtAndLiabilities;
	protected _optionRefs = [ShowAccountNumbersDisplayOption]; // todo - remove this after fixing issues with removal

	constructor(
		route: ActivatedRouteSnapshot,
		datasetsDetail: Array<DatasetDetail>,
		context: ReportContext,
		filedFundNodes?: GarfieldFundNode[],
		localGovernments?: LocalGovernment[],
		public readonly debtReferenceLookups?: { categories: ReferenceLookup<number>[], types: BonanzaSchedule9Type[], items: DebtCategoryItem[] }
	) {
		super(route, datasetsDetail, context, filedFundNodes, localGovernments);
	}
}

export const isApplicable = (report: GarfieldReport | ShallowReport, user: User) => {
	// If report is restricted to global access level then user must have global access
	const matchesGlobalAccessRestriction = (r: GarfieldReport | ShallowReport) => r.restrictedTo === UserAccessLevel.global ? user?.hasGlobalAccess() : true;

	// todo handle case where user has just mcag access when retrofitting for financial report
	// If report is restricted to mcag access level then user must has mcag access
	// const matchesMcagAccessRestriction = (r: GarfieldReport) => r.restrictedTo === UserAccessLevel.mcag ? user?.hasAccessToMcag(governmentId) : true;

	return matchesGlobalAccessRestriction(report);
}

export const isOnlyAnnualFiler = (datasetsDetail: DatasetDetail[]) => (datasetsDetail.length === 1 && (datasetsDetail[0] instanceof SnapshotWithDetail || datasetsDetail[0] instanceof LiveWithDetail))
